import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { updateTenant } from "@/services/authService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import toast from "react-hot-toast";

import {
  createCertificateRequest,
  getCurrentTenantCertificateRequest,
  runCurrentTenantCertificateRequestAutomation,
} from "@/services/certificateRequestsService";
import type { CreateCertificateRequestPayload } from "@/services/certificateRequestsService";
import type { TenantCurrentResponse } from "@/services/authService";
import type { AuthUser } from "@/contexts/AuthContext";

const CURRENT_CERTIFICATE_REQUEST_QUERY_KEY = [
  "tenant-current-certificate-request",
] as const;

interface UseCertificateAcquisitionParams {
  tenant: TenantCurrentResponse | null;
  user: AuthUser | null;
  viewStep: number;
  /** Resolved cert-content (mode="content") viewStep: 5 + preCertOffset. */
  certContentStep: number;
  /** Resolved Abitab / cert-upload viewStep: 6 + preCertOffset. */
  abitabStep: number;
  certificateAcquisitionChoice: "myself" | "fiscalo" | null;
  isAccountantFlowOwner: boolean;
  isAccountantUser: boolean;
  t: TFunction;
}

/**
 * Owns the Fiscalo certificate-acquisition flow: the current-cert-request
 * query + its progress toasts, the step-6 confirm payload (in-memory or
 * derived-from-tenant on refresh), the confirm handler (create request + kick
 * off the Abitab automation + poll it), and the retry handler. The step machine
 * reads `currentCertificateRequest` for its routing/gating and calls
 * `setCertificateStepNextLoading` from the step-6 notarial acknowledgment.
 */
export function useCertificateAcquisition({
  tenant,
  user,
  viewStep,
  certContentStep,
  abitabStep,
  certificateAcquisitionChoice,
  isAccountantFlowOwner,
  isAccountantUser,
  t,
}: UseCertificateAcquisitionParams) {
  const queryClient = useQueryClient();

  const [certificateRequestPayload, setCertificateRequestPayload] = useState<
    CreateCertificateRequestPayload | null
  >(null);
  const [certificateStepNextLoading, setCertificateStepNextLoading] =
    useState(false);
  /** True after user clicks "Confirmar y enviar" on step 6; gates certificate request fetch/poll until then */
  const [, setFiscaloCertificateConfirmed] = useState(false);
  const [certificateAutomationRetrying, setCertificateAutomationRetrying] =
    useState(false);
  /** Tracks previous certificate request state for toast transitions. null = not yet synced (initial load / refresh). */
  const prevCertRequestRef = useRef<{
    status?: string | null;
    idDigitalRequestNumber?: string | null;
  } | null>(null);

  useEffect(() => {
    if (viewStep === certContentStep) {
      setFiscaloCertificateConfirmed(false);
    }
  }, [viewStep, certContentStep]);

  const { data: currentCertificateRequest = null, isLoading: currentCertificateRequestLoading } =
    useQuery({
      queryKey: CURRENT_CERTIFICATE_REQUEST_QUERY_KEY,
      queryFn: getCurrentTenantCertificateRequest,
      enabled:
        (viewStep === certContentStep || viewStep === abitabStep) &&
        certificateAcquisitionChoice === "fiscalo" &&
        !isAccountantFlowOwner &&
        !isAccountantUser,
      refetchInterval:
        viewStep === abitabStep &&
        certificateAcquisitionChoice === "fiscalo" &&
        !isAccountantFlowOwner &&
        !isAccountantUser
          ? 8000
          : false,
    });
  const showFiscaloCertToasts =
    viewStep === abitabStep &&
    certificateAcquisitionChoice === "fiscalo" &&
    !isAccountantFlowOwner &&
    !isAccountantUser;
  useEffect(() => {
    if (!showFiscaloCertToasts || currentCertificateRequestLoading) return;
    const prev = prevCertRequestRef.current;
    const status = currentCertificateRequest?.status ?? null;
    const idDigitalRequestNumber =
      currentCertificateRequest?.idDigitalRequestNumber ?? null;
    const nextRef = { status, idDigitalRequestNumber };
    if (prev === null) {
      prevCertRequestRef.current = nextRef;
      return;
    }
    if (status === "in_progress" && prev.status !== "in_progress") {
      toast.success(t("onboarding.fiscaloProgress.toastFormSubmitted"));
    }
    if (idDigitalRequestNumber && !prev.idDigitalRequestNumber) {
      toast.success(t("onboarding.fiscaloProgress.toastEmailProcessed"));
    }
    prevCertRequestRef.current = nextRef;
  }, [
    showFiscaloCertToasts,
    currentCertificateRequestLoading,
    currentCertificateRequest?.status,
    currentCertificateRequest?.idDigitalRequestNumber,
    t,
  ]);

  /** For the abitab step Fiscalo: use in-memory payload or derive from tenant+user when state was lost (e.g. refresh). */
  const step6FiscaloConfirmPayload = useMemo((): CreateCertificateRequestPayload | null => {
    if (certificateRequestPayload) return certificateRequestPayload;
    if (
      viewStep !== abitabStep ||
      certificateAcquisitionChoice !== "fiscalo" ||
      !tenant ||
      !user
    )
      return null;
    const companyType = tenant.companyType;
    if (
      !companyType ||
      !["unipersonal", "sas", "srl", "sa", "monotributo"].includes(companyType)
    )
      return null;
    const firstName = (user.firstName ?? "").trim();
    const firstLastName = (user.lastName ?? "").trim();
    if (!firstName || !firstLastName) return null;
    return {
      firstName,
      secondName: (user.secondName ?? "").trim() || undefined,
      firstLastName,
      secondLastName: (user.secondLastName ?? "").trim() || undefined,
      durationYears: 1,
      companyType: companyType as CreateCertificateRequestPayload["companyType"],
    };
  }, [
    certificateRequestPayload,
    viewStep,
    abitabStep,
    certificateAcquisitionChoice,
    tenant,
    user,
  ]);

  /** Re-trigger the Abitab automation for an existing cert request that
   *  ended up in `failed`. The user lands on this path when they closed
   *  the automator browser mid-flow or it died after step 1. We don't
   *  recreate the cert request — the row already exists server-side; we
   *  just ask the platform to call /run-automation again. */
  const handleRetryCertificateAutomation = useCallback(async () => {
    if (!currentCertificateRequest?.id) return;
    setCertificateAutomationRetrying(true);
    try {
      await runCurrentTenantCertificateRequestAutomation(
        currentCertificateRequest.id,
      );
      await queryClient.invalidateQueries({
        queryKey: CURRENT_CERTIFICATE_REQUEST_QUERY_KEY,
      });
    } catch {
      toast.error(
        t(
          "onboarding.abitabPresent.retryError",
          "No pudimos reintentar la automatización. Probá de nuevo en unos segundos.",
        ),
      );
    } finally {
      setCertificateAutomationRetrying(false);
    }
  }, [currentCertificateRequest?.id, queryClient, t]);

  const handleConfirmFiscaloCertificate = useCallback(
    async (
      phone?: string,
      tradeName?: string,
    ): Promise<"submitted" | "failed"> => {
      const payload = certificateRequestPayload ?? step6FiscaloConfirmPayload;
      if (!payload || !tenant?.id) return "failed";
      setFiscaloCertificateConfirmed(true);
      setCertificateStepNextLoading(true);
      let createdId: string;
      try {
        const created = await createCertificateRequest(payload);
        createdId = created.id;
      } catch {
        setCertificateStepNextLoading(false);
        return "failed";
      }
      // Stamp "acquisition done" *before* the Abitab automator HTTP call.
      // That ordering is the refresh-safety guarantee: if the user refreshes
      // or closes the tab while the automation is still running, the sync
      // logic routes them past viewStep 5 — the cert request exists
      // server-side and the automation (or a Fiscalo operator) finishes it
      // asynchronously.
      //
      // Persisted on the tenant rather than in sessionStorage, so the same
      // guarantee now holds for a NEW tab or another device, not just this
      // one. Failure is swallowed deliberately: losing the marker costs the
      // user a re-answer, while blocking here would strand a certificate
      // request that is about to be filed for real.
      try {
        await updateTenant({
          onboardingCertAcquisitionDoneAt: new Date().toISOString(),
        });
      } catch {
        // ignore — see above
      }
      await queryClient.invalidateQueries({
        queryKey: CURRENT_CERTIFICATE_REQUEST_QUERY_KEY,
      });
      let automationStarted = true;
      try {
        await runCurrentTenantCertificateRequestAutomation(createdId, {
          phone: phone?.trim() || undefined,
          tradeName: tradeName?.trim() || undefined,
        });
      } catch {
        // automation kickoff failed (e.g. abitab-automator unreachable).
        // The cert request still exists; operator/cron picks it up.
        // Skip the poll loop below since there's nothing to wait for.
        automationStarted = false;
      }
      if (!automationStarted) {
        setCertificateStepNextLoading(false);
        return "submitted";
      }
      // Poll the cert request until the automation reports back via
      // webhook (status flips from pending → in_progress, fulfilled,
      // or failed). This is what keeps the modal's "Procesando" view
      // visible until the automation has actually done work — the
      // previous fire-and-forget version closed after the kickoff
      // call (~1s) and dropped the user onto DGI before the user
      // could read what was happening.
      //
      // Bounded by POLL_TIMEOUT_MS so we never trap the user — if the
      // webhook never fires the modal closes anyway and the
      // dashboard's onboarding card surfaces progress later.
      const POLL_INTERVAL_MS = 2500;
      const POLL_TIMEOUT_MS = 60000;
      const startedAt = Date.now();
      await new Promise<void>((resolve) => {
        const pollId = setInterval(async () => {
          if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            clearInterval(pollId);
            resolve();
            return;
          }
          try {
            const current = await getCurrentTenantCertificateRequest();
            const status = current?.status;
            if (
              status === "in_progress" ||
              status === "failed" ||
              status === "fulfilled"
            ) {
              clearInterval(pollId);
              await queryClient.invalidateQueries({
                queryKey: CURRENT_CERTIFICATE_REQUEST_QUERY_KEY,
              });
              resolve();
            }
          } catch {
            // ignore poll errors — try again next tick
          }
        }, POLL_INTERVAL_MS);
      });
      setCertificateStepNextLoading(false);
      return "submitted";
    },
    [certificateRequestPayload, step6FiscaloConfirmPayload, tenant?.id, queryClient],
  );

  return {
    currentCertificateRequest,
    currentCertificateRequestLoading,
    certificateRequestPayload,
    setCertificateRequestPayload,
    step6FiscaloConfirmPayload,
    certificateStepNextLoading,
    setCertificateStepNextLoading,
    handleConfirmFiscaloCertificate,
    handleRetryCertificateAutomation,
    certificateAutomationRetrying,
  };
}
